import { useOrg } from "@/hooks/org";
import { useGetProductsV1Query } from "@/store/clientApi";
import { selectNumCaseUploads } from "@/store/slices/casesSlice";
import { setIsOutOfCredits } from "@/store/slices/orgSlice";
import { RootState, store } from "@/store/store";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

/**
 * A hook that returns credit information about the current users organisation.
 * @param props.skip - Skip any requests being made. This will cause isOutOfCredits to be false.
 */
export const useOrgCredits = (props?: { skip?: boolean }) => {
  const { skip } = props ?? {};
  const [prevIsOutOfCreditsStatus, setPrevIsOutOfCreditsStatus] = useState(false);

  const { org, isLoading, error, invalidate } = useOrg({ skip });
  const isOutOfCredits = skip ? false : (org?.balance.available ?? 0) <= 0;

  useEffect(() => {
    if (prevIsOutOfCreditsStatus != isOutOfCredits) {
      setPrevIsOutOfCreditsStatus(isOutOfCredits);
      store.dispatch(setIsOutOfCredits(isOutOfCredits));
    }
  }, [prevIsOutOfCreditsStatus, isOutOfCredits]);

  return {
    skipped: skip,
    isOutOfCredits,
    numCredits: org?.balance.available,
    isLoading,
    error,
    invalidate
  };
};

export enum Service {
  ModelBasing
}

/**
 * A hook that calculates the cost of a set of services based on the number of cases processed.
 *
 * This hooks is a placeholder for when future services are added. Only one service is available (model basing).
 *
 * @param props.skip - Skip any requests being made. This will cause totalCost to be 0.
 * @param props.products - A map containing how many cases each service processed.
 */
export const useServiceCost = ({ skip, products }: { skip?: boolean; products: { [id in Service]: number } }) => {
  const { data, isLoading, error } = useGetProductsV1Query(undefined, { skip: skip });

  // TODO: handle different service types
  const totalCost = data ? Object.entries(products).reduce((acc, [_, quantity]) => data.credit_cost_per_case * quantity + acc, 0) : 0;

  return {
    skipped: skip,
    totalCost,
    isLoading,
    error
  };
};

/**
 * This hook returns the total cost to process all cases batched for upload.
 *
 * @param props.skip - Skip any requests being made. This will cause totalCost to be 0.
 */
export const useUploadsServiceCost = (props?: { skip?: boolean }) => {
  const { skip } = props ?? {};
  const numCases = useSelector((state: RootState) => selectNumCaseUploads(state));
  return useServiceCost({
    skip,
    products: {
      // TODO: handle different service types
      [Service.ModelBasing]: numCases
    }
  });
};
